import type { ComputedRef, PropType } from 'vue';
import type { RouteLocationRaw } from 'vue-router'
import type { DimensionProps } from '#ui-composables/useDimension'
import type { SizeProps } from '#ui-composables/useSize'
import type { ThemeProps } from '#ui-composables/useThemes'

const predefinedBtnVariants = ['primary', 'secondary', 'text', 'danger'] as const
export type BtnVariant = typeof predefinedBtnVariants[number]

type CustomBtnProps = {
  variant?: BtnVariant
  inverted?: boolean
} & SizeProps

export type BtnProps = {
  text?: string
  prependIcon?: string
  appendIcon?: string
  block?: boolean
  disabled?: boolean
  active?: boolean
  exact?: boolean
  href?: string
  replace?: boolean
  to?: RouteLocationRaw
  stacked?: boolean
  loading?: boolean
} & DimensionProps & CustomBtnProps & ThemeProps

interface VariantsData extends ThemeProps {
  baseColor?: string
  color?: string
  hoverOpacity?: number
  variant?: 'flat' | 'outlined' | 'text'
  style?: (string | Record<never, never>)[]
  class?: (string | Record<never, never>)[]
}

export const useBtnProps = () => ({
  variant: {
    type: String as PropType<BtnVariant>,
    default: 'primary',
    validator: (value: BtnVariant) => predefinedBtnVariants.includes(value),
  },
  inverted: Boolean,
  text: String,
  prependIcon: String,
  appendIcon: String,
  block: Boolean,
  disabled: Boolean,
  active: Boolean,
  exact: Boolean,
  href: String,
  replace: Boolean,
  to: [String, Object] as PropType<RouteLocationRaw>,
  stacked: Boolean,
  loading: Boolean,
  ...useDimensionProps(),
  ...useSizeProps(),
  ...useThemeProps(),
})

export function useBtn(options: ComputedRef<CustomBtnProps>, props: BtnProps) {
  const isInverted = ref(options.value.inverted)
  const isHovered = ref(false)

  function fontSize(size: SizeProps['size']) {
    return {
      small: 16,
      default: 18,
      large: 20,
    }[size]
  }

  function density(size: SizeProps['size']) {
    return {
      small: 'compact',
      default: 'default',
      large: 'comfortable',
    }[size] || null
  }

  function variants(v: BtnVariant = 'primary') {
    const _v = {
      primary: {
        baseColor: 'btn-primary-default',
        color: isHovered.value ? 'btn-primary-hover' : undefined,
        hoverOpacity: 0,
        class: [],
        style: [],
      },
      secondary: {
        variant: 'outlined',
        baseColor: isInverted.value ? 'white' : 'btn-secondary',
        class: ['border-2'],
        style: [],
      },
      text: {
        variant: 'text',
        baseColor: isInverted.value ? 'btn-ghost-inverted' : 'btn-ghost-default',
        class: ['u-btn--variant-text'],
        style: [{
          '--u-btn-overlay-background': isInverted.value ? 'rgb(var(--v-theme-white))' : 'rgb(var(--v-theme-black))',
        }],
      },
      danger: {
        variant: 'outlined',
        baseColor: 'btn-danger-default',
        class: ['border-2'],
        style: [{
          '--u-btn-overlay-background': 'rgb(var(--v-theme-black))',
        }],
      },
    }

    return _v[v]
  }

  function baseProps() {
    const _fontSize = fontSize(options.value.size)

    return {
      style: [{
        '--v-btn-size': convertToUnit(_fontSize),
      }],
      class: [{
        'outline-secondary-charcoal': !isInverted.value,
        'outline-white': ['secondary', 'text'].includes(options.value.variant!) && isInverted.value,
      }],
    }
  }

  function combinedStyles() {
    const { hoverOpacity, ...variant } = variants(options.value.variant)
    const _baseProps = baseProps()

    return [
      ..._baseProps.style,
      ...(variant.style || []),
      ...(hoverOpacity !== undefined ? [{ '--v-hover-opacity': `${hoverOpacity}` }] : []),
    ]
  }

  function combinedClasses() {
    const { hoverOpacity, ...variant } = variants(options.value.variant)
    const _baseProps = baseProps()

    return [
      ..._baseProps.class,
      ...(variant.class || []),
    ]
  }

  const defaultData = computed(() => {
    const { hoverOpacity, ...variant } = variants(options.value.variant)
    const _combinedStyles = combinedStyles()
    const _density = density(options.value.size) as 'default' | 'comfortable' | 'compact' | null
    const _combinedClasses = combinedClasses()

    return {
      VBtn: {
        ...props,
        ...variant,
        density: _density,
        size: options.value.size,
        style: _combinedStyles,
        class: _combinedClasses,
      },
    }
  })

  function onHover(state: boolean) {
    isHovered.value = state
  }

  return {
    defaultData,
    onHover,
  }
}
